import { useRef, useState, useEffect } from 'react';

const METRICS = [
  { num: '25+', label: 'Years in Operation', icon: 'ri-time-line', desc: 'Two decades of industrial application expertise' },
  { num: '1,000s', label: 'Systems Delivered', icon: 'ri-settings-3-line', desc: 'Installations across the USA and internationally' },
  { num: '12+', label: 'Industries Served', icon: 'ri-building-line', desc: 'From aerospace to mining to electric motor' },
  { num: '100%', label: 'American Built', icon: 'ri-flag-line', desc: 'MAXJET systems built domestic, supported domestic' },
];

const INDUSTRIES = [
  { icon: 'ri-car-line', label: 'Automotive' },
  { icon: 'ri-rocket-line', label: 'Aerospace' },
  { icon: 'ri-building-2-line', label: 'Military' },
  { icon: 'ri-hammer-line', label: 'Mining' },
  { icon: 'ri-plug-line', label: 'Electric Motor' },
  { icon: 'ri-fire-line', label: 'Gas Compressor' },
  { icon: 'ri-ship-line', label: 'Marine' },
  { icon: 'ri-cpu-line', label: 'Manufacturing' },
];

const CERTS = [
  { icon: 'ri-shield-check-line', title: 'ISO-Aligned Quality Systems', desc: 'All systems designed and validated against ISO quality process standards for consistent, measurable performance.' },
  { icon: 'ri-leaf-line', title: 'EPA / OSHA Compliant', desc: 'Aqueous, non-solvent cleaning technology that meets current EPA emission and OSHA workplace safety guidelines.' },
  { icon: 'ri-verified-badge-line', title: 'MAXJET Certified Manufacturer', desc: 'Official MAXJET dealer and builder — American-built, American-supported, with guaranteed domestic part availability.' },
  { icon: 'ri-trophy-line', title: 'Industry Compliance Expertise', desc: 'Deep knowledge of regulatory requirements across automotive, aerospace, and heavy industrial markets.' },
];

export default function AuthoritySection() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative bg-[#060606] py-32 overflow-hidden">

      {/* Dramatic background industrial image — very dark */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9f1bdf36b78b5c8489d92c27fe7eda74.jpg"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#060606]/93" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060606]/80 via-transparent to-[#060606]/80" />
      </div>

      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(201,168,76,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-8 lg:px-12">

        {/* Section header */}
        <div className={`text-center mb-20 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-flex items-center gap-3 text-[#c9a84c] text-[10px] font-bold tracking-[0.5em] uppercase font-sans">
            <span className="h-px w-8 bg-[#c9a84c]" />
            Authority &amp; Credentials
            <span className="h-px w-8 bg-[#c9a84c]" />
          </span>
          <h2 className="font-serif text-5xl lg:text-6xl text-white mt-5 mb-5 leading-tight">
            Operating at the<br />
            <em className="not-italic text-[#c9a84c]">Highest Standard</em>
          </h2>
          <p className="text-white/40 font-sans text-lg max-w-2xl mx-auto">
            25 years of industrial expertise, compliance-driven engineering, and a reputation built entirely on delivered results.
          </p>
        </div>

        {/* Metrics — 4 cards with layered depth */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-5 mb-24 transition-all duration-700 delay-100 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {METRICS.map((m, idx) => (
            <div
              key={idx}
              className="group relative rounded-xl border border-[#1c1c1c] hover:border-[#c9a84c]/25 bg-[#0d0d0d]/90 backdrop-blur-sm overflow-hidden transition-all duration-500 px-6 py-10 text-center"
              style={{ transitionDelay: `${idx * 70}ms` }}
            >
              {/* Hover glow layer */}
              <div className="absolute inset-0 bg-gradient-to-b from-[#c9a84c]/4 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              {/* Corner accent */}
              <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-[#c9a84c]/6 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className="relative z-10">
                <div className="w-13 h-13 flex items-center justify-center mx-auto mb-6 rounded-full bg-[#c9a84c]/8 border border-[#c9a84c]/15 group-hover:bg-[#c9a84c]/14 transition-colors">
                  <i className={`${m.icon} text-[#c9a84c] text-xl`} />
                </div>
                <div className="font-serif text-5xl text-[#c9a84c] font-bold leading-none mb-3">{m.num}</div>
                <p className="text-white font-semibold font-sans text-sm mb-2">{m.label}</p>
                <p className="text-white/30 text-xs font-sans leading-relaxed">{m.desc}</p>
              </div>

              {/* Bottom accent line on hover */}
              <div className="absolute bottom-0 left-1/4 right-1/4 h-px bg-[#c9a84c]/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          ))}
        </div>

        {/* Two-column: Industries + Compliance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20 items-start">

          {/* Industries */}
          <div className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-10 bg-[#c9a84c]" />
              <h3 className="font-serif text-3xl text-white">Industries We Serve</h3>
            </div>
            <p className="text-white/35 font-sans text-sm leading-relaxed mb-8">
              Proven results across the most demanding industrial markets — from Caterpillar equipment dealers to aerospace and military rebuilding operations.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {INDUSTRIES.map((ind, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 px-4 py-3.5 bg-[#0d0d0d] border border-[#1c1c1c] rounded-lg hover:border-[#c9a84c]/20 hover:bg-[#111]/80 transition-all duration-300 group cursor-default"
                  style={{ transitionDelay: `${300 + idx * 40}ms` }}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className={`${ind.icon} text-[#c9a84c]/60 text-base group-hover:text-[#c9a84c] transition-colors`} />
                  </div>
                  <span className="text-white/55 text-sm font-sans group-hover:text-white/80 transition-colors">{ind.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Compliance */}
          <div className={`transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="flex items-center gap-4 mb-8">
              <div className="h-px w-10 bg-[#c9a84c]" />
              <h3 className="font-serif text-3xl text-white">Compliance &amp; Standards</h3>
            </div>
            <p className="text-white/35 font-sans text-sm leading-relaxed mb-8">
              Every system we deliver is built around strict compliance standards — from ISO-aligned processes to EPA-approved aqueous cleaning chemistry.
            </p>
            <div className="space-y-4">
              {CERTS.map((cert, idx) => (
                <div
                  key={idx}
                  className="flex gap-4 p-5 bg-[#0d0d0d] border border-[#1c1c1c] rounded-lg hover:border-[#c9a84c]/20 hover:bg-[#111]/80 transition-all duration-300 group"
                  style={{ transitionDelay: `${400 + idx * 60}ms` }}
                >
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0 rounded-lg bg-[#c9a84c]/8 border border-[#c9a84c]/15 group-hover:bg-[#c9a84c]/14 transition-colors">
                    <i className={`${cert.icon} text-[#c9a84c] text-base`} />
                  </div>
                  <div>
                    <h4 className="font-sans font-bold text-white text-sm mb-1.5">{cert.title}</h4>
                    <p className="text-white/35 text-xs font-sans leading-relaxed">{cert.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
